"use client";

import {
  decodeListState,
  encodeListState,
  type ListState,
} from "@aio/platform-routing";
import type { TableState } from "../use-table-state";

/**
 * URL-shareable subset of {@link TableState}.
 *
 * The full TanStack `TableState` is too rich (column sizing, visibility,
 * selection, etc.) to encode in a hash usefully — and most of those slices
 * are user-machine-local concerns anyway. The shareable surface mirrors
 * {@link ListState} from `@aio/platform-routing`: first sort entry, simple
 * scalar filters, and pagination cursor.
 *
 * Multi-sort beyond the first entry, complex (object-shaped) filter values,
 * row selection, and per-column sizes are intentionally dropped during
 * encoding — they survive across same-tab interactions via `useTableState`'s
 * localStorage persistence, but they don't belong in a shareable URL.
 */
export type ShareableViewState = ListState;

/**
 * Convert a {@link TableState} into the {@link ListState} subset that
 * `encodeListState` understands.
 *
 * - Pagination is normalised to 1-based page numbers (the routing layer
 *   treats `page` as user-facing; TanStack's `pageIndex` is 0-based).
 * - Only the first `sorting` entry is encoded; downstream consumers that
 *   need multi-sort still get it via the in-memory state.
 * - Filters whose value is a non-string (number, boolean, array, …) are
 *   coerced via `String(...)`. Filters whose value is `null`/`undefined`
 *   or an empty string are omitted entirely so the URL stays clean.
 */
export function tableStateToListState(state: TableState): ListState {
  const out: ListState = {};

  // Pagination — only emit when the user has moved off the defaults so
  // a vanilla view produces a shorter URL. The 25-row default mirrors
  // `useTableState`'s seed value.
  if (state.pagination.pageIndex !== 0) {
    out.page = state.pagination.pageIndex + 1;
  }
  if (state.pagination.pageSize !== 25) {
    out.pageSize = state.pagination.pageSize;
  }

  // Sort — first entry only. ListState calls the column "col"; TanStack
  // uses "id". Direction maps directly (`desc: boolean` ↔ "asc" | "desc").
  const firstSort = state.sorting[0];
  if (firstSort) {
    out.sort = {
      col: firstSort.id,
      dir: firstSort.desc ? "desc" : "asc",
    };
  }

  // Filters — flatten to a Record<string, string>. We deliberately skip
  // non-scalar values: encoding a complex JSON value as a query param is
  // beyond the contract `encodeListState` exposes, and the shareable URL
  // is a best-effort affordance, not the durable persistence layer.
  const filters: Record<string, string> = {};
  for (const f of state.columnFilters) {
    const v = f.value;
    if (v === null || v === undefined) continue;
    if (typeof v === "string") {
      if (v.length === 0) continue;
      filters[f.id] = v;
    } else if (typeof v === "number" || typeof v === "boolean") {
      filters[f.id] = String(v);
    }
    // Object/array filter values are dropped on purpose — see doc comment.
  }
  if (Object.keys(filters).length > 0) {
    out.filters = filters;
  }

  return out;
}

/**
 * Apply a {@link ListState} back onto a base {@link TableState}, returning
 * a new state with the slices that `ListState` covers replaced.
 *
 * Slices not represented in `ListState` (column visibility, sizes, order,
 * selection, global filter) are carried over from `base` unchanged so the
 * caller can decode a shared URL on top of whatever local state already
 * exists.
 *
 * Page numbers are converted from 1-based (URL) to 0-based (TanStack).
 * Filters arrive as strings; the column engine is responsible for any
 * type coercion further down the pipeline.
 */
export function listStateToTableState(
  list: ListState,
  base: TableState,
): TableState {
  const next: TableState = {
    ...base,
    pagination: { ...base.pagination },
    sorting: base.sorting,
    columnFilters: base.columnFilters,
  };

  if (list.page !== undefined) {
    // Clamp to ≥0 so a malformed URL with `page=0` doesn't push us into
    // a negative pageIndex. Anything <1 collapses to the first page.
    next.pagination.pageIndex = Math.max(0, list.page - 1);
  }
  if (list.pageSize !== undefined) {
    next.pagination.pageSize = list.pageSize;
  }

  if (list.sort) {
    next.sorting = [{ id: list.sort.col, desc: list.sort.dir === "desc" }];
  }

  if (list.filters) {
    next.columnFilters = Object.entries(list.filters).map(([id, value]) => ({
      id,
      value,
    }));
  }

  return next;
}

/** URL hash prefix. Kept short to minimise sharable-link length. */
const HASH_PREFIX = "#";

/**
 * Encode a {@link TableState} as a URL hash fragment (including the leading
 * `#`). The encoding intentionally goes into the hash, not the search, so
 * routing libraries don't treat the table state as a navigation event and
 * the encoded state never leaks to server logs.
 *
 * Returns just `""` (empty string) when the state is at its defaults so
 * callers can drop the hash entirely from rendered URLs.
 */
export function encodeViewToHash(state: TableState): string {
  const encoded = encodeListState(tableStateToListState(state));
  return encoded.length === 0 ? "" : `${HASH_PREFIX}${encoded}`;
}

/**
 * Decode a URL hash fragment (with or without the leading `#`) into a
 * {@link TableState}, layered on top of `base` so unrepresented slices
 * survive the round-trip.
 *
 * Empty / unparseable hashes return `base` unchanged.
 */
export function decodeHashToView(hash: string, base: TableState): TableState {
  const trimmed = hash.startsWith(HASH_PREFIX) ? hash.slice(1) : hash;
  if (trimmed.length === 0) return base;
  const list = decodeListState(trimmed);
  return listStateToTableState(list, base);
}
